import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/esm/ArrowBack';
import WarningIcon from '@mui/icons-material/esm/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/esm/CheckCircle';
import RefreshIcon from '@mui/icons-material/esm/Autorenew';
import OverrideIcon from '@mui/icons-material/esm/Gavel';
import SaveIcon from '@mui/icons-material/esm/Save';
import QcIcon from '@mui/icons-material/esm/FactCheck';
import { AppDispatch, RootState } from '../../store';
import {
  fetchTaskById,
  fetchTasks,
  updateTaskStatus,
  updateTaskProgress,
  clearCurrentTask,
  setCurrentTaskFromCache,
  overrideTaskStatus,
} from '../../store/slices/tasksSlice';
import { PageHeader } from '../../components/layout';
import { StatusBadge } from '../../components/common';
import tasksService, { Task } from '../../services/api/tasksService';
import taskMaterialsService, { TaskMaterial } from '../../services/api/taskMaterialsService';
import resourceService, { InventorySummaryRow } from '../../services/api/resourceService';
import userService, { UserListItem } from '../../services/api/userService';
import teamService, { isExecutionTeam, TeamListItem } from '../../services/api/teamService';
import procurementService, { SupervisorTeamAssignment } from '../../services/api/procurementService';
import qcChecklistService, { QcRun, QcTemplate } from '../../services/api/qcChecklistService';
import workLocationsService, { WorkLocation as WorkLocationOption } from '../../services/api/workLocationsService';
import {
  LocationBox,
  SpecificationsBox,
  ConditionsBox,
  CreateLocationDialog,
} from '../../components/tasks/TaskFieldEditors';
import { MaterialsBox } from '../../components/tasks/MaterialsBox';
import VoiceInput from '../../components/field/VoiceInput';
import SitePlanMap from '../../components/siteplan/SitePlanMap';
import { cachedTaskById, enqueueOfflineDefect, enqueueOfflineTaskPatch } from '../../services/offline/fieldOfflineForms';
import { workerSpecificationItems } from '../../utils/taskRoleSpecifications';

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  WAITING: 'Waiting',
  BLOCKED: 'Blocked',
  READY: 'Ready',
  IN_PROGRESS: 'In progress',
  PAUSED: 'Paused',
  INSPECTION_PENDING: 'Inspection pending',
  INSPECTION_FAILED: 'Inspection failed',
  INSPECTION_PASSED: 'Inspection passed',
  CANCELLED: 'Cancelled',
};

const FIELD_STATUS_LABELS: Record<string, string> = {
  BLOCKED: 'Blocked Task',
  READY: 'Ready Task',
  IN_PROGRESS: 'In Progress Task',
  INSPECTION_PENDING: 'Task To Be Inspected',
};

// Mirror of backend Task.canTransitionTo (models/Task.ts). Keep in sync.
// Anything not in this map (or empty) requires an Override on the right column.
const VALID_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['SCHEDULED', 'CANCELLED'],
  SCHEDULED: ['WAITING', 'READY', 'BLOCKED', 'CANCELLED'],
  WAITING: ['READY', 'BLOCKED', 'SCHEDULED', 'CANCELLED'],
  BLOCKED: ['WAITING', 'READY', 'CANCELLED'],
  READY: ['IN_PROGRESS', 'BLOCKED', 'WAITING'],
  IN_PROGRESS: ['PAUSED', 'BLOCKED', 'INSPECTION_PENDING'],
  PAUSED: ['IN_PROGRESS', 'BLOCKED'],
  INSPECTION_PENDING: ['IN_PROGRESS', 'INSPECTION_FAILED', 'INSPECTION_PASSED'],
  INSPECTION_FAILED: ['INSPECTION_PASSED'],
  INSPECTION_PASSED: [],
  CANCELLED: [],
};

const FIELD_STATUS_OPTIONS = ['READY', 'BLOCKED', 'IN_PROGRESS', 'INSPECTION_PENDING'];

const OVERRIDE_TYPES = [
  { value: 'force_ready', label: 'Force Ready' },
  { value: 'force_complete', label: 'Force Complete' },
  { value: 'force_pass', label: 'Force Pass' },
  { value: 'force_uncancel', label: 'Force Uncancel' },
  { value: 'force_reopen', label: 'Force Reopen' },
  { value: 'force_cancel', label: 'Force Cancel' },
];

const REASON_CODES = [
  { value: 'scope_error', label: 'Scope error' },
  { value: 'client_change', label: 'Client change' },
  { value: 'safety_concern', label: 'Safety concern' },
  { value: 'materials_unavailable_indefinitely', label: 'Materials unavailable indefinitely' },
  { value: 'team_unavailable_indefinitely', label: 'Team unavailable indefinitely' },
  { value: 'dependency_obsolete', label: 'Dependency obsolete' },
  { value: 'regulatory_change', label: 'Regulatory change' },
  { value: 'weather_indefinite', label: 'Weather indefinite' },
  { value: 'pm_judgement', label: 'PM judgement' },
];

const assignedCrewNameFor = (task: { team?: { name?: string | null } | null; teamAssignments?: any[] }) => {
  const activeAssignment = (task.teamAssignments ?? []).find((assignment) => {
    const status = String(assignment.lifecycleStatus ?? assignment.lifecycle_status ?? '').toLowerCase();
    return !['cancelled', 'removed'].includes(status);
  });
  return activeAssignment?.team?.name ?? activeAssignment?.teamName ?? activeAssignment?.team_name ?? task.team?.name ?? 'Unassigned';
};

const assignedCrewIdFor = (task: { team?: { id?: string | null } | null; teamAssignments?: any[] }) => {
  const activeAssignment = (task.teamAssignments ?? []).find((assignment) => {
    const status = String(assignment.lifecycleStatus ?? assignment.lifecycle_status ?? '').toLowerCase();
    return !['cancelled', 'removed'].includes(status);
  });
  return activeAssignment?.teamId ?? activeAssignment?.team_id ?? activeAssignment?.team?.id ?? task.team?.id ?? '';
};

const userDisplayName = (person?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null) => {
  const name = `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim();
  return name || person?.email || 'Supervisor';
};

interface MaterialStockSummary {
  available: number;
  inUse: number;
  total: number;
  unit: string;
  lowStock: boolean;
  belowSafetyStock: boolean;
}

const materialStockSummaries = (rows: InventorySummaryRow[]) => {
  const summaries = new Map<string, MaterialStockSummary>();
  rows.forEach((row) => {
    const current = summaries.get(row.resourceId) ?? {
      available: 0,
      inUse: 0,
      total: 0,
      unit: row.unitOfMeasure,
      lowStock: false,
      belowSafetyStock: false,
    };
    current.available += Number(row.quantityAvailable || 0);
    current.inUse += Number(row.quantityInUse || 0);
    current.total += Number(row.totalQuantity || 0);
    current.lowStock = current.lowStock || row.lowStock;
    current.belowSafetyStock = current.belowSafetyStock || row.belowSafetyStock;
    if (!current.unit && row.unitOfMeasure) current.unit = row.unitOfMeasure;
    summaries.set(row.resourceId, current);
  });
  return summaries;
};

const stockUsedPercent = (summary?: MaterialStockSummary | null) =>
  summary && summary.total > 0 ? Math.round((100 * summary.inUse) / summary.total) : null;

const formatQuantity = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

interface TaskDetailPageProps {
  fieldMode?: boolean;
}

export default function TaskDetailPage({ fieldMode = false }: TaskDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch() as AppDispatch;
  const { currentTask, loading, error } = useSelector((state: RootState) => state.tasks);
  const user = useSelector((state: RootState) => state.auth.user);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newProgress, setNewProgress] = useState(0);
  const [overrideType, setOverrideType] = useState('force_ready');
  const [reasonCode, setReasonCode] = useState('pm_judgement');
  const [overrideNotes, setOverrideNotes] = useState('');

  const [workLocations, setWorkLocations] = useState<WorkLocationOption[]>([]);
  const [createLocationOpen, setCreateLocationOpen] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const [paymentAmountDraft, setPaymentAmountDraft] = useState<string>('');
  const [paymentChoice, setPaymentChoice] = useState<'paid' | 'not_paid'>('not_paid');
  const [defectDraft, setDefectDraft] = useState<string>('');
  const [qcRuns, setQcRuns] = useState<QcRun[]>([]);
  const [qcTemplates, setQcTemplates] = useState<QcTemplate[]>([]);
  const [qcDialogOpen, setQcDialogOpen] = useState(false);
  const [selectedQcTemplateId, setSelectedQcTemplateId] = useState('');
  const [qcLoading, setQcLoading] = useState(false);
  const [designMaterials, setDesignMaterials] = useState<TaskMaterial[]>([]);
  const [inventorySummary, setInventorySummary] = useState<InventorySummaryRow[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [teamSaving, setTeamSaving] = useState(false);
  const [supervisors, setSupervisors] = useState<UserListItem[]>([]);
  const [supervisorAssignments, setSupervisorAssignments] = useState<SupervisorTeamAssignment[]>([]);
  const [supervisorDialogOpen, setSupervisorDialogOpen] = useState(false);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
  const [supervisorNotes, setSupervisorNotes] = useState('');
  const [supervisorSaving, setSupervisorSaving] = useState(false);

  const refreshLocations = async () => {
    try {
      const list = await workLocationsService.list();
      setWorkLocations(list);
    } catch {
      // Non-fatal — picker just shows "no locations yet".
    }
  };

  const reloadTask = () => {
    if (id) dispatch(fetchTaskById(id));
  };

  const loadQcRuns = async () => {
    if (!id) return;
    try {
      const rows = await qcChecklistService.listRunsForTask(id);
      setQcRuns(rows);
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to load QC runs');
    }
  };

  const openQcDialog = async () => {
    if (!currentTask) return;
    try {
      setQcLoading(true);
      setFieldError(null);
      const rows = await qcChecklistService.listTemplates({
        projectId: currentTask.projectId ?? undefined,
        taskType: currentTask.taskType,
        active: true,
      });
      setQcTemplates(rows);
      setSelectedQcTemplateId(rows[0]?.id ?? '');
      setQcDialogOpen(true);
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to load QC templates');
    } finally {
      setQcLoading(false);
    }
  };

  const startQcRun = async () => {
    if (!id || !selectedQcTemplateId) return;
    try {
      const run = await qcChecklistService.startRun(id, selectedQcTemplateId);
      setQcDialogOpen(false);
      navigate(`${fieldMode ? '/field/qc-runs' : '/qc-runs'}/${run.id}`);
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to start QC run');
    }
  };

  const handlePickLocation = async (locationId: string | null) => {
    if (!id) return;
    try {
      await tasksService.updateTask(id, { workLocationId: locationId });
      reloadTask();
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || 'Failed to update task location');
    }
  };

  const handleCreateLocation = async (name: string) => {
    const created = await workLocationsService.create({ name });
    await refreshLocations();
    if (id) {
      await tasksService.updateTask(id, { workLocationId: created.id });
      reloadTask();
    }
    setCreateLocationOpen(false);
  };

  const handleUpdateSpecifications = async (
    _taskId: string,
    specifications: Array<{ text: string; done: boolean }>
  ) => {
    if (!id) return;
    try {
      await tasksService.updateTask(id, { specifications } as any);
      reloadTask();
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || 'Failed to update specifications');
    }
  };

  const handleUpdatePriority = async (priority: 'LOW' | 'MEDIUM' | 'HIGH') => {
    if (!id) return;
    try {
      await tasksService.updateTask(id, { priority } as any);
      reloadTask();
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || 'Failed to update priority');
    }
  };

  const handleUpdateConditions = async (_taskId: string, conditions: string[]) => {
    if (!id) return;
    try {
      await tasksService.updateTask(id, { conditions } as any);
      reloadTask();
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || 'Failed to update conditions');
    }
  };

  const handleSavePayment = async () => {
    if (!id) return;
    const amountNum = paymentAmountDraft === '' ? null : Number(paymentAmountDraft);
    if (amountNum !== null && (!Number.isFinite(amountNum) || amountNum < 0)) {
      setFieldError('Payment amount must be a non-negative number');
      return;
    }
    try {
      await tasksService.updateTask(id, {
        paymentStatus: paymentChoice === 'paid' ? 'paid' : 'not_eligible',
        paymentAmount: amountNum,
      } as any);
      reloadTask();
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || 'Failed to update payment');
    }
  };

  const handleToggleMaterials = async (ready: boolean) => {
    if (!id) return;
    try {
      await tasksService.updateTask(id, { materialsReady: ready } as any);
      reloadTask();
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || 'Failed to update materials readiness');
    }
  };

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  /**
   * "Save all" — collects the current draft values for the editable
   * fields on this page (payment amount + status, materials readiness,
   * priority, plus everything already in currentTask) and PUTs them in
   * a single request. Useful as a safety net even though most editors
   * auto-save on change.
   */
  const handleSaveAll = async () => {
    if (!id || !currentTask) return;
    setSaving(true);
    setFieldError(null);
    setSaveSuccess(null);
    try {
      const amountNum = paymentAmountDraft === '' ? null : Number(paymentAmountDraft);
      const payload: Record<string, unknown> = {
        // From draft state (may differ from saved currentTask):
        paymentStatus: paymentChoice === 'paid' ? 'paid' : 'not_eligible',
        paymentAmount: amountNum != null && Number.isFinite(amountNum) ? amountNum : null,
        // From currentTask — re-syncs anything that may have drifted:
        materialsReady: currentTask.materialsReady ?? false,
        priority: currentTask.priority,
        specifications: currentTask.specifications ?? [],
        conditions: (currentTask as any).conditions ?? [],
        workLocationId: currentTask.workLocationId ?? null,
      };
      await tasksService.updateTask(id, payload as any);
      reloadTask();
      setSaveSuccess('Saved.');
    } catch (err: any) {
      setFieldError(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDefect = async () => {
    if (!id || !currentTask) return;
    const text = defectDraft.trim();
    if (!text) return;
    try {
      if (fieldMode && !navigator.onLine) {
        throw new Error('offline');
      }
      await tasksService.addDefect(id, { title: text, severity: 'medium' });
      setDefectDraft('');
      reloadTask();
    } catch (err: any) {
      if (fieldMode) {
        const result = await enqueueOfflineDefect(currentTask, { title: text, severity: 'medium' });
        dispatch(setCurrentTaskFromCache(result.task));
        setDefectDraft('');
        setSaveSuccess('Defect saved offline and will sync when the device reconnects.');
        setFieldError(null);
      } else {
        setFieldError(err?.response?.data?.error || 'Failed to add defect');
      }
    }
  };

  const appendDefectDraft = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setDefectDraft((current) => current.trim() ? `${current.trim()} ${trimmed}` : trimmed);
  };
  const userRole = String(user?.role || '').toLowerCase();
  const canManageCrewAssignments = ['admin', 'field_admin', 'pm'].includes(userRole);
  const isSupervisorFieldDetail = fieldMode && userRole === 'supervisor';
  const isPmFieldDetail = fieldMode && canManageCrewAssignments;

  useEffect(() => {
    let cancelled = false;
    if (id) {
      (async () => {
        if (fieldMode && !navigator.onLine) {
          const cached = await cachedTaskById(id);
          if (!cancelled && cached) dispatch(setCurrentTaskFromCache(cached));
          return;
        }
        const result = await dispatch(fetchTaskById(id));
        if (!cancelled && fieldMode && (result as any).meta?.requestStatus === 'rejected') {
          const cached = await cachedTaskById(id);
          if (cached) dispatch(setCurrentTaskFromCache(cached));
        }
      })();
    }
    return () => {
      cancelled = true;
      dispatch(clearCurrentTask());
    };
  }, [dispatch, id, fieldMode]);

  useEffect(() => {
    if (!isPmFieldDetail) return;
    let cancelled = false;
    const selectedProjectId = currentTask?.projectId ?? undefined;
    Promise.all([
      userService.searchUsers({ role: 'supervisor', limit: 50 }),
      teamService.getTeams(),
      selectedProjectId
        ? procurementService.listSupervisorAssignments({ projectId: selectedProjectId, isActive: true })
        : Promise.resolve([]),
    ])
      .then(([supervisorRows, teamRows, assignmentRows]) => {
        if (cancelled) return;
        const activeTeams = teamRows.filter((team) => team.isActive !== false && isExecutionTeam(team));
        const assignedTeamId = currentTask ? assignedCrewIdFor(currentTask) : '';
        const initialTeamId = assignedTeamId || activeTeams[0]?.id || '';
        const recommendedSupervisorId = assignmentRows.find((assignment) => assignment.teamId === initialTeamId)?.supervisorUserId;
        setSupervisors(supervisorRows);
        setTeams(activeTeams);
        setSupervisorAssignments(assignmentRows);
        setSelectedSupervisorId((current) => current || recommendedSupervisorId || supervisorRows[0]?.id || '');
        setSelectedTeamId((current) => current || initialTeamId);
      })
      .catch(() => {
        if (!cancelled) {
          setSupervisors([]);
          setTeams([]);
          setSupervisorAssignments([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isPmFieldDetail, currentTask?.projectId, currentTask?.team?.id, currentTask?.teamAssignments]);

  useEffect(() => {
    if (id) loadQcRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (currentTask) {
      setNewProgress(currentTask.percentComplete);
      // Leave the Status dropdown empty so the user must explicitly pick a
      // legal NEXT state. Pre-seeding with the current state was misleading
      // because the same state is never a valid transition target.
      setNewStatus('');
      const amount = (currentTask as any).paymentAmount;
      setPaymentAmountDraft(amount != null ? String(amount) : '');
      setPaymentChoice(currentTask.paymentStatus === 'paid' ? 'paid' : 'not_paid');
    }
  }, [currentTask]);

  useEffect(() => {
    refreshLocations();
  }, []);

  useEffect(() => {
    let cancelled = false;
    resourceService.getInventorySummary()
      .then((rows) => {
        if (!cancelled) setInventorySummary(rows);
      })
      .catch(() => {
        if (!cancelled) setInventorySummary([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!currentTask?.id) {
      setDesignMaterials([]);
      return;
    }
    let cancelled = false;
    taskMaterialsService.listForTask(currentTask.id)
      .then((rows) => {
        if (!cancelled) setDesignMaterials(rows);
      })
      .catch(() => {
        if (!cancelled) setDesignMaterials([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentTask?.id]);

  const stockByResourceId = useMemo(
    () => materialStockSummaries(inventorySummary),
    [inventorySummary]
  );

  const lifecycleStatus = currentTask?.lifecycleStatus || currentTask?.status || 'DRAFT';
  const statusOptions = useMemo(() => {
    const transitions = VALID_TRANSITIONS[lifecycleStatus] || [];
    return fieldMode
      ? transitions.filter((value) => FIELD_STATUS_OPTIONS.includes(value))
      : transitions;
  }, [fieldMode, lifecycleStatus]);
  const statusLabelFor = (value: string) =>
    (fieldMode ? FIELD_STATUS_LABELS[value] : undefined) || STATUS_LABELS[value] || value.replace(/_/g, ' ');
  const defects = currentTask?.defects || [];
  const history = currentTask?.history || [];
  const selectedTeam = useMemo(
    () => teams.find((team) => team.id === selectedTeamId) ?? null,
    [teams, selectedTeamId]
  );
  const selectedCrewSupervisorAssignment = useMemo(
    () =>
      supervisorAssignments.find(
        (assignment) => assignment.teamId === selectedTeamId && assignment.isActive !== false
      ) ?? null,
    [supervisorAssignments, selectedTeamId]
  );
  const recommendedSupervisor =
    supervisors.find((supervisor) => supervisor.id === selectedCrewSupervisorAssignment?.supervisorUserId) ??
    selectedCrewSupervisorAssignment?.supervisor ??
    null;

  const defectSummary = useMemo(
    () => ({
      count: defects.length,
      open: defects.filter((defect) => !['closed', 'resolved'].includes(String(defect.status || '').toLowerCase())).length,
    }),
    [defects]
  );

  const handleStatusChange = async () => {
    if (!id || !newStatus || !currentTask) return;
    setFieldError(null);
    setSaveSuccess(null);
    if (fieldMode && !navigator.onLine) {
      const updated = await enqueueOfflineTaskPatch(
        currentTask,
        { lifecycleStatus: newStatus.toLowerCase() },
        { lifecycleStatus: newStatus, status: newStatus }
      );
      dispatch(setCurrentTaskFromCache(updated));
      setSaveSuccess('Task status saved offline and will sync when the device reconnects.');
      setStatusDialogOpen(false);
      return;
    }
    const result = await dispatch(updateTaskStatus({ id, status: newStatus }));
    if ((result as any).meta?.requestStatus === 'rejected') {
      // The transition was rejected (likely by the state machine). Keep the
      // dialog open and surface the backend's error so the user can pick a
      // legal transition — or use the Override button on the right column.
      setFieldError(
        ((result as any).payload as string) ||
          'Status change failed. Try Override if the transition is locked by the state machine.'
      );
      return;
    }
    // Also refresh the Dispatch Board list so the task moves to its new
    // accordion section when the user navigates back. The slice already
    // updates state.tasks for this id, but a full refetch is the safest
    // way to keep groupings consistent (covers cases where readiness
    // re-eval, defect auto-create, etc. side-effected the list).
    dispatch(fetchTasks({}));
    setStatusDialogOpen(false);
  };

  const handleProgressUpdate = async () => {
    if (id && currentTask) {
      if (fieldMode && !navigator.onLine) {
        const updated = await enqueueOfflineTaskPatch(
          currentTask,
          { completionPercentage: newProgress },
          { percentComplete: newProgress }
        );
        dispatch(setCurrentTaskFromCache(updated));
        setSaveSuccess('Task progress saved offline and will sync when the device reconnects.');
      } else {
        const result = await dispatch(updateTaskProgress({ id, percentComplete: newProgress }));
        if (fieldMode && (result as any).meta?.requestStatus === 'rejected') {
          setFieldError(((result as any).payload as string) || 'Failed to update progress');
        }
      }
      setProgressDialogOpen(false);
    }
  };

  const handleOverride = async () => {
    if (!id) return;
    await dispatch(
      overrideTaskStatus({
        id,
        payload: {
          overrideType,
          reasonCode,
          notes: overrideNotes,
        },
      })
    );
    setOverrideDialogOpen(false);
    setOverrideNotes('');
  };

  const openSupervisorDialog = () => {
    setSelectedSupervisorId(recommendedSupervisor?.id || currentTask?.pmIssuedToSupervisorId || supervisors[0]?.id || '');
    setSupervisorNotes('');
    setSupervisorDialogOpen(true);
  };

  const submitSupervisorHandoff = async () => {
    if (!id || !currentTask || !selectedSupervisorId) return;
    setSupervisorSaving(true);
    setFieldError(null);
    setSaveSuccess(null);
    try {
      if (selectedTeamId && selectedTeamId !== assignedCrewIdFor(currentTask)) {
        await tasksService.assignTeam(id, selectedTeamId);
      }
      const updated = await tasksService.issueToSupervisor(id, {
        supervisorUserId: selectedSupervisorId,
        notes: supervisorNotes.trim() || null,
      });
      const supervisorName = updated.pmIssuedToSupervisor
        ? `${updated.pmIssuedToSupervisor.firstName} ${updated.pmIssuedToSupervisor.lastName}`.trim()
        : 'Supervisor';
      setSaveSuccess(`${updated.taskCode || updated.title} sent to ${supervisorName} with ${assignedCrewNameFor(updated)} assigned.`);
      setSupervisorDialogOpen(false);
      reloadTask();
    } catch (err: any) {
      setFieldError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to send task to supervisor.');
    } finally {
      setSupervisorSaving(false);
    }
  };

  const assignSelectedTeam = async () => {
    if (!id || !selectedTeamId) return;
    setTeamSaving(true);
    setFieldError(null);
    setSaveSuccess(null);
    try {
      const updated = await tasksService.assignTeam(id, selectedTeamId);
      setSaveSuccess(`Assigned to ${assignedCrewNameFor(updated)}.`);
      reloadTask();
    } catch (err: any) {
      setFieldError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to assign team.');
    } finally {
      setTeamSaving(false);
    }
  };

  if (loading && !currentTask) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography color="text.secondary">Loading task detail...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(fieldMode ? '/field/tasks' : '/tasks')}>
          Back to Dispatch Board
        </Button>
      </Box>
    );
  }

  if (!currentTask) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Task not found
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(fieldMode ? '/field/tasks' : '/tasks')}>
          Back to Dispatch Board
        </Button>
      </Box>
    );
  }

  const workerSpecs = workerSpecificationItems(currentTask as Task);

  if (isPmFieldDetail) {
    const issuedSupervisorName = currentTask.pmIssuedToSupervisor
      ? `${currentTask.pmIssuedToSupervisor.firstName} ${currentTask.pmIssuedToSupervisor.lastName}`.trim()
      : null;
    const specs = workerSpecs;

    return (
      <Box>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={900}>{currentTask.title}</Typography>
              <Typography variant="caption" color="text.secondary">{currentTask.taskCode || currentTask.id}</Typography>
            </Box>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/field/pm/lookahead')}>
              Back
            </Button>
          </Stack>

          {fieldError && (
            <Alert severity="error" onClose={() => setFieldError(null)}>
              {fieldError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" onClose={() => setSaveSuccess(null)}>
              {saveSuccess}
            </Alert>
          )}

          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={900}>Materials</Typography>
                <Typography variant="caption" color="text.secondary">Confirm whether Stores can issue what this crew needs.</Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={!currentTask.materialsReady ? 'contained' : 'outlined'}
                  color={!currentTask.materialsReady ? 'warning' : 'inherit'}
                  onClick={() => handleToggleMaterials(false)}
                >
                  Not Ready
                </Button>
                <Button
                  variant={currentTask.materialsReady ? 'contained' : 'outlined'}
                  color={currentTask.materialsReady ? 'success' : 'inherit'}
                  onClick={() => handleToggleMaterials(true)}
                >
                  Ready
                </Button>
              </Stack>
            </Stack>
            {designMaterials.length ? (
              <Stack spacing={0.75}>
                {designMaterials.map((material) => {
                  const uom = material.resource?.unitOfMeasure ?? '';
                  const stock = stockByResourceId.get(material.resourceId);
                  const percent = stockUsedPercent(stock);
                  const stockUnit = stock?.unit || uom;
                  return (
                    <Paper key={material.id} variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Typography aria-hidden="true" sx={{ fontWeight: 900, lineHeight: 1.4 }}>□</Typography>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={900}>{material.resource?.name ?? material.resourceId}</Typography>
                          {stock ? (
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              <Chip size="small" variant="outlined" label={`${formatQuantity(stock.available)} ${stockUnit} available`} />
                              <Chip size="small" variant="outlined" label={`${formatQuantity(stock.inUse)} ${stockUnit} in use`} />
                              {percent !== null && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 108 }}>
                                  <Typography variant="caption" fontWeight={900}>{percent}%</Typography>
                                  <LinearProgress variant="determinate" value={percent} sx={{ width: 72, height: 6, borderRadius: 1 }} />
                                </Box>
                              )}
                            </Stack>
                          ) : (
                            <Chip size="small" variant="outlined" color="warning" label="No stock record" sx={{ mt: 0.5 }} />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Required: {Number(material.quantityRequired)} {uom}
                          </Typography>
                          {stock?.lowStock || stock?.belowSafetyStock ? (
                            <Typography variant="caption" color="warning.main" display="block">
                              {stock.belowSafetyStock ? 'Below safety stock' : 'Low stock'}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No materials are linked to this task yet.</Typography>
            )}
          </Paper>


          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="h6" fontWeight={900}>Crew Assignment</Typography>
                <Typography variant="caption" color="text.secondary">
                  Assign the team that is supposed to do the work before sending it to the supervisor.
                </Typography>
              </Box>
              <TextField
                select
                fullWidth
                label="Work team"
                value={selectedTeamId}
                onChange={(event) => setSelectedTeamId(event.target.value)}
                disabled={teams.length === 0}
              >
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </TextField>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Button
                  variant="contained"
                  onClick={assignSelectedTeam}
                  disabled={teamSaving || !selectedTeamId}
                  sx={{ fontWeight: 900 }}
                >
                  {teamSaving ? 'Assigning...' : 'Assign Team'}
                </Button>
                <Chip size="small" label={`Current: ${assignedCrewNameFor(currentTask)}`} />
              </Stack>
              {selectedTeamId && (
                <Alert severity={recommendedSupervisor ? 'info' : 'warning'} sx={{ py: 0.75 }}>
                  {recommendedSupervisor
                    ? `${selectedTeam?.name ?? 'This crew'} is normally supervised by ${userDisplayName(recommendedSupervisor)}.`
                    : `${selectedTeam?.name ?? 'This crew'} does not have a linked supervisor yet.`}
                </Alert>
              )}
            </Stack>
          </Paper>

          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            <Stack spacing={1.25}>
              <Typography variant="h6" fontWeight={900}>Task Location</Typography>
              <LocationBox
                task={currentTask}
                locations={workLocations}
                onPicked={handlePickLocation}
                onCreateRequest={() => setCreateLocationOpen(true)}
                onLocationDeleted={refreshLocations}
              />
              <SitePlanMap task={currentTask} compact />
            </Stack>
          </Paper>

          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>Specifications</Typography>
            {specs.length > 0 ? (
              <Stack spacing={0.75}>
                {specs.map((spec, index) => (
                  <Typography key={`${spec.text}-${index}`} variant="body2">
                    {spec.done ? '✓' : '•'} {spec.text}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No specifications are linked to this task.</Typography>
            )}
          </Paper>

          <Paper sx={{ p: 1.5, borderRadius: 1 }}>
            {issuedSupervisorName && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Already sent to {issuedSupervisorName}.
              </Alert>
            )}
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={openSupervisorDialog}
              disabled={supervisors.length === 0}
              sx={{ fontWeight: 900 }}
            >
              {issuedSupervisorName ? 'Reissue to Supervisor' : 'Send to Supervisor'}
            </Button>
          </Paper>
        </Stack>

        <Dialog open={supervisorDialogOpen} onClose={() => setSupervisorDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Send Task to Supervisor</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This sends the task, assigned crew, materials, and location details to the selected supervisor.
              </Typography>
              {selectedTeamId && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Stack spacing={0.5}>
                    <Typography variant="overline" color="text.secondary" fontWeight={900}>
                      Crew Supervisor
                    </Typography>
                    <Typography variant="body1" fontWeight={900}>
                      {selectedTeam?.name ?? 'Selected crew'}
                    </Typography>
                    {recommendedSupervisor ? (
                      <>
                        <Typography variant="body2" fontWeight={900}>
                          {userDisplayName(recommendedSupervisor)} is the supervisor for this crew.
                        </Typography>
                        {recommendedSupervisor.email && (
                          <Typography variant="caption" color="text.secondary">
                            {recommendedSupervisor.email}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          The supervisor below is preselected from this crew link, but can be changed for site coverage.
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="warning.main" fontWeight={900}>
                        No default supervisor is linked to this crew. Choose the supervisor covering site duties.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}
              <TextField
                select
                fullWidth
                label="Supervisor"
                value={selectedSupervisorId}
                onChange={(event) => setSelectedSupervisorId(event.target.value)}
              >
                {supervisors.map((supervisor) => (
                  <MenuItem key={supervisor.id} value={supervisor.id}>
                    {supervisor.firstName} {supervisor.lastName} - {supervisor.email}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Instruction notes"
                value={supervisorNotes}
                onChange={(event) => setSupervisorNotes(event.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSupervisorDialogOpen(false)} disabled={supervisorSaving}>Cancel</Button>
            <Button variant="contained" onClick={submitSupervisorHandoff} disabled={supervisorSaving || !selectedSupervisorId}>
              {supervisorSaving ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={currentTask.title}
        subtitle={currentTask.taskCode || currentTask.id}
        breadcrumbs={[{ label: 'Dispatch Board', path: fieldMode ? '/field/tasks' : '/tasks' }, { label: 'Task Detail' }]}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => id && dispatch(fetchTaskById(id))}>
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
              disabled={saving}
              sx={{ display: fieldMode ? 'none' : undefined }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="outlined" startIcon={<OverrideIcon />} onClick={() => setOverrideDialogOpen(true)} sx={{ display: fieldMode ? 'none' : undefined }}>
              Override
            </Button>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(fieldMode ? '/field/tasks' : '/tasks')}>
              Back
            </Button>
          </Stack>
        }
      />

      {/* Task Summary + Progress moved here per Martin's preview ask —
          immediately below the task name (PageHeader title) and above the
          editable KPI row. */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 1, height: '100%' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Task Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
              {currentTask.description || 'No description provided'}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <StatusBadge status={lifecycleStatus} />
              {!fieldMode && <StatusBadge status={currentTask.priority} type="priority" />}
              {currentTask.team?.name && <Chip size="small" variant="outlined" label={currentTask.team.name} />}
            </Stack>
            {fieldMode && !isSupervisorFieldDetail && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Controls
                </Typography>
                <Button variant="contained" onClick={() => setStatusDialogOpen(true)}>
                  Change Status
                </Button>
              </>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: fieldMode ? 'none' : 'block' }}>
          <Paper sx={{ p: 2, borderRadius: 1, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                Progress
              </Typography>
              <Button size="small" startIcon={<CheckCircleIcon />} onClick={() => setProgressDialogOpen(true)}>
                Update
              </Button>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={currentTask.percentComplete}
              sx={{ height: 8, borderRadius: 99 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {currentTask.percentComplete}% complete
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Priority — editable Low/Medium/High */}
        <Grid item xs={12} md={3} sx={{ display: fieldMode ? 'none' : 'block' }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Priority
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={
                    currentTask.priority === 'LOW' || currentTask.priority === 'MEDIUM' || currentTask.priority === 'HIGH'
                      ? currentTask.priority
                      : 'MEDIUM'
                  }
                  onChange={(e) => handleUpdatePriority(e.target.value as 'LOW' | 'MEDIUM' | 'HIGH')}
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Payment — editable amount + Paid/Not paid select */}
        <Grid item xs={12} md={3} sx={{ display: fieldMode ? 'none' : 'block' }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Payment
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  type="number"
                  label="Amount"
                  value={paymentAmountDraft}
                  onChange={(e) => setPaymentAmountDraft(e.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                  sx={{ flex: 1, minWidth: 90 }}
                />
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <Select
                    value={paymentChoice}
                    onChange={(e) => setPaymentChoice(e.target.value as 'paid' | 'not_paid')}
                  >
                    <MenuItem value="not_paid">Not paid</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Button
                size="small"
                onClick={handleSavePayment}
                sx={{ mt: 1 }}
              >
                Save payment
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Materials — Ready / Not ready select */}
        <Grid item xs={12} md={fieldMode ? 12 : 3} sx={{ display: isSupervisorFieldDetail ? 'none' : 'block' }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Materials
              </Typography>
              {fieldMode ? (
                <Stack spacing={1}>
                  <Button
                    variant={!currentTask.materialsReady ? 'contained' : 'outlined'}
                    color={!currentTask.materialsReady ? 'warning' : 'inherit'}
                    onClick={() => handleToggleMaterials(false)}
                    fullWidth
                  >
                    Not ready
                  </Button>
                  <Button
                    variant={currentTask.materialsReady ? 'contained' : 'outlined'}
                    color={currentTask.materialsReady ? 'success' : 'inherit'}
                    onClick={() => handleToggleMaterials(true)}
                    fullWidth
                  >
                    Ready
                  </Button>
                </Stack>
              ) : (
                <FormControl size="small" fullWidth>
                  <Select
                    value={currentTask.materialsReady ? 'ready' : 'not_ready'}
                    onChange={(e) => handleToggleMaterials(e.target.value === 'ready')}
                  >
                    <MenuItem value="not_ready">Not ready</MenuItem>
                    <MenuItem value="ready">Ready</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Manual override — readiness evaluator may recompute if procurement state changes.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Defects — count + quick-add text input */}
        <Grid item xs={12} md={3} sx={{ display: fieldMode ? 'none' : 'block' }}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Defects
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <WarningIcon color={defectSummary.open ? 'error' : 'disabled'} fontSize="small" />
                  <Typography variant="h6" fontWeight={700}>
                    {defectSummary.open}/{defectSummary.count}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Describe a defect..."
                    value={defectDraft}
                    onChange={(e) => setDefectDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddDefect();
                      }
                    }}
                  />
                  {fieldMode && <VoiceInput onText={appendDefectDraft} />}
                </Stack>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddDefect}
                  disabled={!defectDraft.trim()}
                >
                  Add
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {fieldError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFieldError(null)}>
          {fieldError}
        </Alert>
      )}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(null)}>
          {saveSuccess}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            {!isSupervisorFieldDetail && (
              <LocationBox
                task={currentTask}
                locations={workLocations}
                onPicked={handlePickLocation}
                onCreateRequest={() => setCreateLocationOpen(true)}
                onLocationDeleted={refreshLocations}
              />
            )}

            {fieldMode && (
              <Paper sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  Task Details
                </Typography>
                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Team</Typography>
                    <Typography variant="body2" fontWeight={800}>{assignedCrewNameFor(currentTask)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Due</Typography>
                    <Typography variant="body2" fontWeight={800}>{currentTask.dueDate ? new Date(currentTask.dueDate).toLocaleDateString() : 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Updated</Typography>
                    <Typography variant="body2" fontWeight={800}>{new Date(currentTask.updatedAt).toLocaleString()}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {fieldMode ? (
              <Paper sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Specifications
                </Typography>
                <Stack spacing={0.75}>
                  {workerSpecs.map((spec, index) => (
                    <Typography key={`${spec.text}-${index}`} variant="body2">
                      {spec.done ? '[x]' : '-'} {spec.text}
                    </Typography>
                  ))}
                  {workerSpecs.length === 0 && (
                    <Alert severity="info">No specifications are linked to this task.</Alert>
                  )}
                </Stack>
              </Paper>
            ) : (
              <SpecificationsBox
                task={currentTask}
                onChange={handleUpdateSpecifications}
              />
            )}

            {!fieldMode && (
              <ConditionsBox
                task={currentTask}
                onChange={handleUpdateConditions}
              />
            )}

            <MaterialsBox taskId={currentTask.id} showYardZone={!fieldMode} />

            {currentTask.notes && (
              <Paper sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {currentTask.notes}
                </Typography>
              </Paper>
            )}

            <Paper sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Defects
              </Typography>
              <Stack spacing={1.5}>
                {defects.map((defect: any) => (
                  <Paper key={defect.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {defect.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {defect.description || 'No defect description provided'}
                        </Typography>
                      </Box>
                      <Stack spacing={1} alignItems="flex-end">
                        <StatusBadge status={defect.status || 'open'} type="custom" customColor="error" />
                        <Chip size="small" variant="outlined" label={String(defect.severity || 'medium')} />
                      </Stack>
                    </Stack>
                    {defect.remedialTaskId && (
                      <Typography variant="caption" color="text.secondary">
                        Remedial task: {defect.remedialTaskId}
                      </Typography>
                    )}
                  </Paper>
                ))}
                {defects.length === 0 && (
                  <Alert severity="info">No defects are linked to this task.</Alert>
                )}
              </Stack>
            </Paper>

            {!fieldMode && (
              <Paper sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Recent Activity
                </Typography>
                <Stack spacing={1}>
                  {history.slice(0, 5).map((entry: any, index: number) => (
                    <Box key={entry.id || index}>
                      <Typography variant="body2" fontWeight={600}>
                        {entry.action || entry.eventType || 'event'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.notes || entry.message || entry.reason || 'No details'}
                      </Typography>
                    </Box>
                  ))}
                  {history.length === 0 && (
                    <Alert severity="info">No history entries were returned for this task.</Alert>
                  )}
                </Stack>
              </Paper>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            {!fieldMode && <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Controls
                </Typography>
                <Stack spacing={1}>
                  <Button variant="outlined" onClick={() => setStatusDialogOpen(true)} fullWidth>
                    Change Status
                  </Button>
                  <Button variant="outlined" onClick={() => setProgressDialogOpen(true)} fullWidth>
                    Update Progress
                  </Button>
                  <Button variant="outlined" startIcon={<QcIcon />} onClick={openQcDialog} fullWidth disabled={qcLoading}>
                    Run QC Checklist
                  </Button>
                  <Tooltip title="Force a status transition that the state machine would otherwise block. Use when the regular Change Status is rejected.">
                    <Button variant="outlined" onClick={() => setOverrideDialogOpen(true)} fullWidth>
                      Override
                    </Button>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>}

            {!fieldMode && <Paper sx={{ p: 2, borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  QC Runs
                </Typography>
                <Tooltip title="Refresh QC runs">
                  <Button size="small" onClick={loadQcRuns}>Refresh</Button>
                </Tooltip>
              </Stack>
              <Stack spacing={1}>
                {qcRuns.map((run) => (
                  <Paper key={run.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1, cursor: 'pointer' }} onClick={() => navigate(`${fieldMode ? '/field/qc-runs' : '/qc-runs'}/${run.id}`)}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700}>{run.runCode}</Typography>
                        <Typography variant="caption" color="text.secondary">{run.template?.name ?? 'QC checklist'}</Typography>
                      </Box>
                      <Stack spacing={0.5} alignItems="flex-end">
                        <Chip size="small" label={run.status.replace(/_/g, ' ')} />
                        {run.overallResult && <Chip size="small" color={run.overallResult === 'fail' ? 'error' : run.overallResult === 'pass' ? 'success' : 'warning'} label={run.overallResult.replace(/_/g, ' ')} />}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
                {qcRuns.length === 0 && <Alert severity="info">No QC runs have been started for this task.</Alert>}
              </Stack>
            </Paper>}

            {!fieldMode && <Paper sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Task Details
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Team:</strong> {assignedCrewNameFor(currentTask)}
                </Typography>
                <Typography variant="body2">
                  <strong>Due:</strong> {currentTask.dueDate ? new Date(currentTask.dueDate).toLocaleDateString() : 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Updated:</strong> {new Date(currentTask.updatedAt).toLocaleString()}
                </Typography>
              </Stack>
            </Paper>}
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change task lifecycle</DialogTitle>
        <DialogContent>
          {fieldError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setFieldError(null)}>
              {fieldError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Current: <strong>{STATUS_LABELS[lifecycleStatus] || lifecycleStatus}</strong>
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }} disabled={statusOptions.length === 0}>
            <InputLabel>Move to</InputLabel>
            <Select value={newStatus} label="Move to" onChange={(event) => setNewStatus(event.target.value)}>
              {statusOptions.map((value) => (
                <MenuItem key={value} value={value}>
                  {statusLabelFor(value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {(VALID_TRANSITIONS[lifecycleStatus] || []).length === 0
              ? <>No normal transitions available from <strong>{STATUS_LABELS[lifecycleStatus] || lifecycleStatus}</strong>. Use <strong>Override</strong> on the right column.</>
              : <>Only legal next states are shown. Need a different jump (e.g. Draft → Ready)? Close this dialog and use <strong>Override</strong>.</>}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusChange} variant="contained" disabled={!newStatus}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={progressDialogOpen} onClose={() => setProgressDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update progress</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Progress %"
              type="number"
              value={newProgress}
              onChange={(event) => setNewProgress(Math.min(100, Math.max(0, Number(event.target.value))))}
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgressDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleProgressUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override task state</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Override type</InputLabel>
              <Select value={overrideType} label="Override type" onChange={(event) => setOverrideType(event.target.value)}>
                {OVERRIDE_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Reason code</InputLabel>
              <Select value={reasonCode} label="Reason code" onChange={(event) => setReasonCode(event.target.value)}>
                {REASON_CODES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes"
              multiline
              minRows={3}
              value={overrideNotes}
              onChange={(event) => setOverrideNotes(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleOverride} variant="contained" disabled={!overrideNotes.trim()}>
            Apply override
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={qcDialogOpen} onClose={() => setQcDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run QC Checklist</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Template" value={selectedQcTemplateId} onChange={(event) => setSelectedQcTemplateId(event.target.value)} disabled={qcTemplates.length === 0}>
              {qcTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name} (v{template.version})
                </MenuItem>
              ))}
            </TextField>
            {qcTemplates.length === 0 && (
              <Alert severity="info">No active QC templates match this task type.</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQcDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<QcIcon />} onClick={startQcRun} disabled={!selectedQcTemplateId}>
            Start
          </Button>
        </DialogActions>
      </Dialog>

      <CreateLocationDialog
        open={createLocationOpen}
        onClose={() => setCreateLocationOpen(false)}
        onSubmit={handleCreateLocation}
      />
    </Box>
  );
}
